import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, FlaskRound, BookOpen, Building2, Calendar, Eye } from 'lucide-react';
import api from '../../api';
import SubjectActionsModal from './SubjectDetails';
import FacultyAttendanceModal from './FacultyAttendanceModal';

const ClassDetailsModal = ({ isOpen, onClose, facultyId, academicYear }) => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClassAction, setSelectedClassAction] = useState(null);
  const [viewOnlyData, setViewOnlyData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceSession, setAttendanceSession] = useState(null);
  const [attendanceRefreshToken, setAttendanceRefreshToken] = useState(0);

  useEffect(() => {
    if (isOpen && facultyId) {
      setLoading(true);
      setErrorMsg("");
      console.log('[ClassDetailsModal] Fetching classes for facultyId:', facultyId);
      api.get(`/api/faculty/${facultyId}/classes-subjects`)
        .then(res => {
          console.log('[ClassDetailsModal] API response:', res);
          if (res.data?.success) {
            const classList = res.data.data.classes || [];
            console.log('[ClassDetailsModal] Classes received:', classList);
            setClasses(classList);
            if (classList.length === 0 && res.data.message) {
              setErrorMsg(res.data.message);
            }
          } else if (res.data?.message) {
            setClasses([]);
            setErrorMsg(res.data.message);
          }
        })
        .catch(err => {
          setErrorMsg("Error fetching classes. Please try again later.");
          setClasses([]);
          console.error("[ClassDetailsModal] Error fetching classes:", err)
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, facultyId]);

  // ============================================================
  // FIX: Properly explode lab batches with batchId
  // ============================================================
  const explodedClasses = useMemo(() => {
    const result = classes.map(cls => {
      const newSubjects = [];
      (cls.assignedSubjects || []).forEach(sub => {
        if (sub.isLab && Array.isArray(sub.labBatches) && sub.labBatches.length > 0) {
          sub.labBatches.forEach(batch => {
            // Ensure batch has proper ID fields
            const batchId = batch._id || batch.batchId || batch.id;
            const batchNumber = batch.batchNumber || batch.number || batch.name || 'Unknown';
            
            newSubjects.push({ 
              ...sub, 
              labBatch: {
                ...batch,
                // Explicitly set batchId for consistent access
                batchId: batchId,
                _id: batchId,
                batchNumber: batchNumber,
                // Preserve all original batch data
                originalBatch: batch
              } 
            });
          });
        } else {
          newSubjects.push(sub);
        }
      });
      return { ...cls, assignedSubjects: newSubjects };
    });
    return result;
  }, [classes]);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return explodedClasses;
    const term = searchTerm.toLowerCase();
    return explodedClasses.filter(cls => 
      cls.className?.toLowerCase().includes(term) || 
      cls.assignedSubjects?.some(sub => sub.name.toLowerCase().includes(term))
    );
  }, [explodedClasses, searchTerm]);

  // ============================================================
  // FIX: View Only mode - properly pass batch info
  // ============================================================
  const handleViewOnlySubject = async (cls, subject, labBatch) => {
    try {
      setLoading(true);
      console.log('[ClassDetailsModal] Fetching students for view-only mode:', { 
        classId: cls.classId, 
        subjectId: subject.subjectId,
        batchId: labBatch?.batchId || labBatch?._id,
        batchNumber: labBatch?.batchNumber
      });
      
      // Build URL with batchId if available
      let url = `/api/classes/${cls.classId}/subjects/${subject.subjectId}/students`;
      if (labBatch?.batchId || labBatch?._id) {
        const batchId = labBatch.batchId || labBatch._id;
        url += `?batchId=${batchId}`;
        console.log('[ClassDetailsModal] Adding batch filter:', batchId);
      }
      
      const response = await api.get(url);
      
      if (response.data?.success) {
        setViewOnlyData({
          classItem: cls,
          subject: subject,
          labBatch: labBatch,
          students: response.data.data.students?.list || [],
          academicContext: response.data.data.academicContext,
          summary: response.data.data.summary
        });
      }
    } catch (error) {
      console.error('[ClassDetailsModal] Error fetching students:', error);
      alert('Failed to fetch student data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isClassReadOnly = (cls) => {
    return cls.readOnly === true;
  };

  // ============================================================
  // FIX: Handle subject click - properly pass batch info
  // ============================================================
  const handleSubjectClick = (cls, sub, labBatch, e) => {
    // Prevent event bubbling
    if (e) e.stopPropagation();
    
    // Determine if class is read-only
    const readOnly = isClassReadOnly(cls);
    
    if (readOnly) {
      // View only mode
      handleViewOnlySubject(cls, sub, labBatch);
    } else {
      // Normal navigation - pass all data including labBatch
      const classId = cls.classId || cls._id;
      const subjectId = sub.subjectId || sub._id;
      
      // Ensure labBatch has batchId for the target component
      const labBatchData = labBatch ? {
        ...labBatch,
        batchId: labBatch.batchId || labBatch._id || labBatch.id,
        _id: labBatch._id || labBatch.batchId || labBatch.id
      } : undefined;
      
      console.log('[ClassDetailsModal] Navigating to subject details:', {
        classId,
        subjectId,
        labBatch: labBatchData,
        batchId: labBatchData?.batchId
      });
      
      navigate('/faculty/class-subject-details', { 
        state: { 
          classItem: cls, 
          subject: sub, 
          labBatch: labBatchData, 
          facultyId, 
          classId, 
          subjectId,
          // Explicitly pass batchId for the target component
          batchId: labBatchData?.batchId
        } 
      });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 transition-opacity" onClick={onClose} />
      
      <div className="fixed inset-y-0 right-0 w-full sm:max-w-xl lg:max-w-2xl bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
        
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between bg-white sticky top-0 z-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Assigned Classes</h2>
            <div className="flex items-center gap-2 mt-1">
              <Calendar className="w-3.5 h-3.5 text-indigo-500" />
              <p className="text-sm font-medium text-indigo-600">{academicYear || 'Current Session'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        <div className="px-8 py-4 bg-slate-50 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by class name or subject..."
              className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50/30">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mb-4"></div>
              <p className="text-sm font-medium">Loading classes...</p>
            </div>
          ) : errorMsg ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 px-8 text-center">
              <div className="bg-slate-100 p-6 rounded-full mb-4">
                <Calendar className="w-10 h-10 text-slate-300" />
              </div>
              <p className="text-lg font-semibold text-rose-600">{errorMsg}</p>
              <p className="text-sm mt-1 max-w-xs">If you believe this is a mistake, please contact your college admin.</p>
            </div>
          ) : (classes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 px-8 text-center">
              <div className="bg-slate-100 p-6 rounded-full mb-4">
                <Search className="w-10 h-10 text-slate-300" />
              </div>
              <p className="text-lg font-semibold text-slate-600">No class assignments found</p>
              <p className="text-sm mt-1 max-w-xs">We couldn't find any classes assigned to you for this academic year.</p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {filtered.map((cls, idx) => {
                const readOnly = isClassReadOnly(cls);
                return (
                  <div key={idx} className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${readOnly ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200 hover:shadow-md'}`}>
                    <div className={`px-6 py-4 border-b flex items-center justify-between ${readOnly ? 'bg-amber-50/50' : 'bg-slate-50/80'}`}>
                      <div className="flex items-center gap-4">
                        <div className="p-2.5 bg-white rounded-xl border border-slate-200 shadow-sm">
                          <Building2 className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-lg font-bold text-slate-800 leading-tight">{cls.className}</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                              {cls.academicYear?.name || academicYear || 'Academic Year'}
                            </span>
                            {(cls.semesterInfo || cls.termInfo) && (
                              <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                            )}
                            {cls.semesterInfo && (cls.semesterInfo.semesterName || cls.semesterInfo.label) ? (
                              <span className="text-xs font-bold text-indigo-600 uppercase">
                                {cls.semesterInfo.semesterName || cls.semesterInfo.label}
                              </span>
                            ) : cls.termInfo && (cls.termInfo.termLabel || cls.termInfo.label || cls.termInfo.termName) ? (
                              <span className="text-xs font-bold text-green-700 uppercase">
                                {cls.termInfo.termLabel || cls.termInfo.label || cls.termInfo.termName}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      {readOnly && (
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200">
                            <Eye className="w-3 h-3 inline mr-1" />
                            Read Only
                          </span>
                          <span className="text-xs text-amber-600">
                            {cls.statusMessage || 'Completed Academic Year'}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="divide-y divide-slate-100">
                      {cls.assignedSubjects?.map((sub, sIdx) => (
                        <div
                          key={sIdx}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Pass the labBatch to the handler
                            handleSubjectClick(cls, sub, sub.labBatch, e);
                          }}
                          className={`flex items-center justify-between px-6 py-5 transition-colors ${
                            readOnly ? 'bg-white/50 cursor-pointer hover:bg-amber-50/50' : 'hover:bg-indigo-50/30 cursor-pointer'
                          }`}
                        >
                          <div className="flex items-center gap-5 min-w-0">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold shadow-sm transition-transform group-hover:scale-105 ${sub.isLab ? 'bg-purple-100 text-purple-600' : 'bg-indigo-100 text-indigo-600'}`}> 
                              {sub.isLab ? <FlaskRound className="w-6 h-6" /> : <BookOpen className="w-6 h-6" />}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <h4 className="font-semibold text-slate-900 group-hover:text-indigo-700 transition-colors text-base break-words leading-snug">
                                {sub.name}
                              </h4>
                              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                {sub.code && (
                                  <span className="text-xs font-mono font-bold text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 uppercase">
                                    {sub.code}
                                  </span>
                                )}
                                {/* ============================================================
                                    FIX: Show batch number with proper data
                                    ============================================================ */}
                                {sub.isLab && sub.labBatch && (
                                  <span className="bg-purple-600 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-tighter uppercase">
                                    Batch {sub.labBatch.batchNumber || sub.labBatch.number || sub.labBatch.name || 'Unknown'}
                                  </span>
                                )}
                                {sub.credits > 0 && (
                                  <span className="text-xs font-medium text-slate-400">{sub.credits} Credits</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">
                            <button
                              className={`text-sm font-bold px-4 py-2 rounded-lg border shadow-sm transition-all ${
                                readOnly 
                                  ? 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200' 
                                  : 'text-indigo-600 bg-white border-indigo-100 hover:bg-indigo-600 hover:text-white'
                              }`}
                            >
                              {readOnly ? 'View Only' : 'Manage'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="px-8 py-4 bg-white border-t border-slate-200 flex justify-between items-center text-xs font-medium text-slate-500">
          <span>Active Academic Year: {academicYear}</span>
          <span className="bg-slate-100 px-3 py-1 rounded-full">{filtered.length} Classes Listed</span>
        </div>

        {selectedClassAction && (
          <SubjectActionsModal
            isOpen={!!selectedClassAction}
            onClose={() => setSelectedClassAction(null)}
            classItem={selectedClassAction.classItem}
            subject={selectedClassAction.subject}
            facultyId={facultyId}
            labBatch={selectedClassAction.labBatch}
            attendanceRefreshToken={attendanceRefreshToken}
            onMarkAttendance={(session) => {
              setAttendanceSession(session);
              setShowAttendanceModal(true);
              setSelectedClassAction(null);
            }}
          />
        )}

        {showAttendanceModal && attendanceSession && (
          <FacultyAttendanceModal
            isOpen={showAttendanceModal}
            onClose={() => setShowAttendanceModal(false)}
            onSuccess={() => setAttendanceRefreshToken(t => t + 1)}
            session={attendanceSession}
            facultyId={facultyId}
          />
        )}

        {/* View Only Modal - FIX: Show batch info */}
        {viewOnlyData && (
          <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b bg-amber-50 flex items-center justify-between sticky top-0">
                <div>
                  <h3 className="text-xl font-bold text-amber-800 flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    View Only Mode
                  </h3>
                  <p className="text-sm text-amber-600 mt-1">
                    {viewOnlyData.classItem.className} - {viewOnlyData.subject.name}
                    {viewOnlyData.labBatch && (
                      <span className="ml-2 bg-purple-600 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-tighter uppercase">
                        Batch {viewOnlyData.labBatch.batchNumber || viewOnlyData.labBatch.number || viewOnlyData.labBatch.name || 'Unknown'}
                      </span>
                    )}
                  </p>
                  {viewOnlyData.academicContext && (
                    <p className="text-xs text-amber-500 mt-1">
                      {viewOnlyData.academicContext.message}
                    </p>
                  )}
                </div>
                <button 
                  onClick={() => setViewOnlyData(null)} 
                  className="p-2 hover:bg-amber-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-amber-700" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-slate-800">Student Enrollment</h4>
                    <span className="text-sm bg-slate-100 px-3 py-1 rounded-full">
                      Total: {viewOnlyData.students?.length || 0} Students
                    </span>
                  </div>
                  
                  {viewOnlyData.students && viewOnlyData.students.length > 0 ? (
                    <div className="border rounded-xl overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">USN</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Student Name</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewOnlyData.students.map((student, idx) => (
                            <tr key={idx} className="border-t hover:bg-slate-50">
                              <td className="px-4 py-3 text-sm font-mono text-slate-700">{student.USN || '-'}</td>
                              <td className="px-4 py-3 text-sm text-slate-700">{student.name}</td>
                              <td className="px-4 py-3 text-sm text-slate-500">{student.email || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <p>No students found for this subject</p>
                    </div>
                  )}
                </div>

                {viewOnlyData.summary && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                    <h5 className="text-sm font-semibold text-slate-700 mb-2">Enrollment Summary</h5>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">From Assignments:</span>
                        <span className="font-semibold">{viewOnlyData.summary.sources?.fromAssignments || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">From Lab Batches:</span>
                        <span className="font-semibold">{viewOnlyData.summary.sources?.fromLabBatches || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">From Elective Groups:</span>
                        <span className="font-semibold">{viewOnlyData.summary.sources?.fromElectiveGroups || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Response Time:</span>
                        <span className="font-semibold">{viewOnlyData.summary.responseTime || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="px-6 py-4 border-t bg-slate-50 flex justify-end">
                <button
                  onClick={() => setViewOnlyData(null)}
                  className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ClassDetailsModal;